import { useEffect, useMemo, useRef, useState } from 'react'
import CommentaryTicketCard from './CommentaryTicketCard'
import SegmentedControl from '../Shared/SegmentedControl'
import { useApp } from '../../context/AppContext'
import useHasAccess from '../../hooks/useHasAccess'
import useTicketCatalogs from '../../hooks/useTicketCatalogs'
import { fileService } from '../../services/fileService'
import { THREAD_FILTERS, buildThread } from '../../utils/commentThread'

/**
 * Hilo de mensajes de un ticket, estilo chat: separadores por día, mensajes
 * agrupados por autor, los propios a la derecha y filtro Todos / Públicos /
 * Internos (las notas internas sólo las ve soporte). Se desplaza solo al
 * último mensaje.
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.ticketId
 * @param {Array<Object>} props.commentaries - Comentarios del ticket.
 * @returns {JSX.Element}
 */
const CommentaryList = ({ ticketId, commentaries }) => {
  const { user } = useApp()
  const { employeeByAuthId } = useTicketCatalogs()
  const isAgent = useHasAccess('ticket_support')
  const [filter, setFilter] = useState('all')
  const [files, setFiles] = useState([])
  const scrollRef = useRef(null)

  // Las notas internas sólo son visibles para agentes de soporte.
  const visible = useMemo(
    () => commentaries.filter((c) => isAgent || !c.internal),
    [commentaries, isAgent]
  )
  const shown = useMemo(
    () => visible.filter(THREAD_FILTERS[filter] || THREAD_FILTERS.all),
    [visible, filter]
  )
  const thread = useMemo(() => buildThread(shown), [shown])
  const internalCount = visible.filter((c) => c.internal).length

  // Adjuntos de los comentarios; se recargan cuando llega un mensaje nuevo.
  useEffect(() => {
    let active = true
    fileService
      .getCommentsFilesByTicketId(ticketId)
      .then(({ success, data, error }) => {
        if (!active) return
        if (success) setFiles(data || [])
        else console.error('Error fetching files:', error)
      })
    return () => {
      active = false
    }
  }, [ticketId, commentaries.length])

  // Al cargar o recibir mensajes, mostrar el más reciente.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [thread.length])

  const filterOptions = [
    { value: 'all', label: 'Todos', count: visible.length },
    {
      value: 'public',
      label: 'Públicos',
      count: visible.length - internalCount,
    },
    ...(isAgent
      ? [{ value: 'internal', label: 'Internos', count: internalCount }]
      : []),
  ]

  return (
    <div className="cml">
      <div className="cml-head">
        <h6 className="cml-title">
          <i className="fa-regular fa-comments" />
          Conversación
        </h6>
        {visible.length > 0 && (
          <SegmentedControl
            ariaLabel="Filtrar mensajes"
            options={filterOptions}
            value={filter}
            onChange={setFilter}
          />
        )}
      </div>

      <div className="cml-scroll" ref={scrollRef}>
        {thread.length === 0 ? (
          <div className="cml-empty">
            <i className="fa-regular fa-comment-dots" />
            <span>
              {visible.length === 0
                ? 'Aún no hay mensajes. Escriba el primero abajo.'
                : 'No hay mensajes con este filtro.'}
            </span>
          </div>
        ) : (
          thread.map((item) =>
            item.kind === 'day' ? (
              <div key={item.key} className="cml-day">
                <span>{item.label}</span>
              </div>
            ) : (
              <CommentaryTicketCard
                key={item.key}
                commentary={item.comment}
                author={employeeByAuthId[item.comment.userId]}
                files={files.filter((f) => f.commentId === item.comment.id)}
                isOwn={item.comment.userId === user?.id}
                startsGroup={item.startsGroup}
              />
            )
          )
        )}
      </div>

      <style>{`
        .cml { display: flex; flex-direction: column; gap: 10px; }
        .cml-head { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 8px; }
        .cml-title {
          display: flex; align-items: center; gap: 8px; margin: 0;
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em;
          text-transform: uppercase; color: var(--gm-text-muted);
        }
        .cml-title i { color: var(--gm-primary); font-size: 0.8rem; }
        .cml-head .seg { height: 30px; }
        .cml-head .seg-btn { font-size: 0.74rem; padding: 0 8px; }
        .cml-scroll { max-height: 460px; overflow-y: auto; padding: 4px 2px; }
        .cml-empty {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          padding: 28px 12px; font-size: 0.82rem; color: var(--gm-text-faint);
        }
        .cml-empty i { font-size: 1.4rem; opacity: 0.6; }
        .cml-day {
          display: flex; align-items: center; gap: 10px; margin: 14px 0 8px;
          font-size: 0.68rem; font-weight: 600; color: var(--gm-text-faint);
        }
        .cml-day:first-child { margin-top: 2px; }
        .cml-day::before, .cml-day::after { content: ''; flex: 1; height: 1px; background: var(--gm-border); }
        .cml-day span { padding: 2px 10px; border-radius: 99px; background: var(--gm-surface-2); }

        /* Mensaje */
        .cm { display: flex; gap: 8px; margin-top: 2px; }
        .cm.starts-group { margin-top: 10px; }
        .cm.is-own { flex-direction: row-reverse; }
        .cm-avatar { width: 30px; flex-shrink: 0; }
        .cm.is-own .cm-avatar { display: none; }
        .cm-body { display: flex; flex-direction: column; max-width: min(78%, 640px); min-width: 0; }
        .cm.is-own .cm-body { align-items: flex-end; }
        .cm-meta { display: flex; align-items: center; gap: 8px; margin: 0 4px 3px; }
        .cm-name { font-size: 0.78rem; font-weight: 600; color: var(--gm-text); }
        .cm-internal-badge {
          display: inline-flex; align-items: center; gap: 4px; padding: 1px 7px; border-radius: 99px;
          font-size: 0.6rem; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
          color: var(--accent); background: rgba(247, 148, 29, 0.15);
        }
        .cm-bubble {
          position: relative; padding: 7px 12px 18px; border-radius: 14px; border-top-left-radius: 4px;
          background: var(--gm-surface-2); color: var(--gm-text); min-width: 72px;
        }
        .cm:not(.starts-group) .cm-bubble { border-top-left-radius: 14px; }
        .cm.is-own .cm-bubble {
          border-top-left-radius: 14px; border-top-right-radius: 4px;
          background: var(--gm-primary-soft);
        }
        .cm.is-own:not(.starts-group) .cm-bubble { border-top-right-radius: 14px; }
        .cm-bubble.is-internal {
          background: rgba(245, 158, 11, 0.1); box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.35);
        }
        .cm-text { margin: 0; font-size: 0.86rem; line-height: 1.45; white-space: pre-wrap; word-break: break-word; }
        .cm-time {
          position: absolute; right: 10px; bottom: 3px;
          font-size: 0.62rem; color: var(--gm-text-faint); font-variant-numeric: tabular-nums;
        }
        .cm-mention {
          color: var(--gm-primary); font-weight: 600; background: var(--gm-primary-soft);
          border-radius: 4px; padding: 0 3px;
        }
        .cm-images { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
        .cm-image {
          padding: 0; border: 1px solid var(--gm-border); border-radius: 10px; overflow: hidden;
          background: transparent; cursor: zoom-in; line-height: 0;
        }
        .cm-image img { max-height: 160px; max-width: 240px; object-fit: cover; transition: opacity 0.15s ease; }
        .cm-image:hover img { opacity: 0.85; }
        .cm-files { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
        .cm-file {
          display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 8px;
          background: var(--gm-surface); border: 1px solid var(--gm-border); font-size: 0.78rem;
        }
        .cm-file > i { color: var(--gm-primary); }
        .cm-file-name { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
        .cm-file-size { color: var(--gm-text-faint); flex-shrink: 0; }
        .cm-file-btn {
          width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center;
          border: none; border-radius: 6px; background: transparent; color: var(--gm-text-muted); font-size: 0.72rem;
        }
        .cm-file-btn:first-of-type { margin-left: auto; }
        .cm-file-btn:hover { background: var(--gm-surface-2); color: var(--gm-text); }

        /* Visor de imágenes */
        .cm-viewer {
          position: fixed; inset: 0; z-index: 2000; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 12px; padding: 24px;
          background: rgba(15, 23, 42, 0.88);
        }
        .cm-viewer img { max-width: 100%; max-height: 80vh; object-fit: contain; border-radius: 8px; }
        .cm-viewer-bar {
          display: flex; align-items: center; gap: 6px; max-width: 100%;
          padding: 6px 8px 6px 14px; border-radius: 10px; background: rgba(255, 255, 255, 0.1);
          color: #fff; font-size: 0.82rem;
        }
        .cm-viewer-size { opacity: 0.6; flex-shrink: 0; margin-right: 6px; }
        .cm-viewer-bar button, .cm-viewer-bar a {
          width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center;
          border: none; border-radius: 7px; background: transparent; color: #fff;
        }
        .cm-viewer-bar button:hover, .cm-viewer-bar a:hover { background: rgba(255, 255, 255, 0.15); }

        @media (max-width: 575px) {
          .cm-body { max-width: 88%; }
        }
      `}</style>
    </div>
  )
}

export default CommentaryList
